import { useCallback, useEffect, useRef, useState } from "react";
import { Bot, MessageSquare, Mic, MicOff, Volume2, X } from "lucide-react";
import { useLang } from "../context/LangContext";
import { useDialysisAssistant } from "../hooks/useDialysisAssistant";
import { useAssistantVoice } from "../hooks/useAssistantVoice";
import AssistantPendingLogAction from "./AssistantPendingLogAction";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";

export default function FloatingAssistant() {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [pendingQuestion, setPendingQuestion] = useState("");
  const messagesEndRef = useRef(null);
  const messageIdRef = useRef(0);
  const {
    question,
    setQuestion,
    error,
    loading,
    usageCount,
    dailyLimit,
    remaining,
    askAssistant,
    pendingLogAction,
    confirmPendingLogAction,
    cancelPendingLogAction,
  } = useDialysisAssistant();
  const [voiceQuestionToAsk, setVoiceQuestionToAsk] = useState("");
  const handleTranscript = useCallback(
    (transcript) => {
      const voiceQuestion = transcript.trim();
      setQuestion(voiceQuestion);
      if (voiceQuestion && !loading && remaining > 0) setVoiceQuestionToAsk(voiceQuestion);
    },
    [loading, remaining, setQuestion]
  );
  const {
    listening,
    recording,
    transcribing,
    speaking,
    speechLoading,
    voiceError,
    startListening,
    stopListening,
    speak,
    stopSpeaking,
    clearVoiceError,
  } = useAssistantVoice({ onTranscript: handleTranscript });

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, pendingQuestion, loading]);

  const handleAsk = useCallback(async (questionOverride) => {
    clearVoiceError();
    const submittedQuestion = (typeof questionOverride === "string" ? questionOverride : question).trim();
    if (submittedQuestion) {
      setPendingQuestion(submittedQuestion);
    }

    const result = await askAssistant(submittedQuestion);
    setPendingQuestion("");

    if (result?.ok) {
      setMessages((currentMessages) => [
        ...currentMessages,
        { id: ++messageIdRef.current, role: "user", text: result.question },
        { id: ++messageIdRef.current, role: "assistant", text: result.answer },
      ]);
    }
    return result;
  }, [askAssistant, clearVoiceError, question]);

  useEffect(() => {
    if (!voiceQuestionToAsk) return;
    if (loading || remaining === 0) {
      setVoiceQuestionToAsk("");
      return;
    }

    const askAndSpeak = async () => {
      const voiceQuestion = voiceQuestionToAsk;
      setVoiceQuestionToAsk("");
      const result = await handleAsk(voiceQuestion);
      if (result?.ok) {
        await speak(result.answer, { silentError: true });
      }
    };

    askAndSpeak();
  }, [handleAsk, loading, remaining, speak, voiceQuestionToAsk]);

  const handleTypedChange = (event) => {
    clearVoiceError();
    setQuestion(event.target.value);
  };

  const handleMicStart = (event) => {
    event.preventDefault();
    startListening();
  };

  const handleMicStop = (event) => {
    event.preventDefault();
    if (recording) stopListening();
  };

  const handleConfirmPendingLog = async () => {
    const result = await confirmPendingLogAction();
    if (result?.message) {
      setMessages((currentMessages) => [
        ...currentMessages,
        { id: ++messageIdRef.current, role: "assistant", text: result.message },
      ]);
    }
  };

  const handleCancelPendingLog = () => {
    const result = cancelPendingLogAction();
    if (result?.message) {
      setMessages((currentMessages) => [
        ...currentMessages,
        { id: ++messageIdRef.current, role: "assistant", text: result.message },
      ]);
    }
  };

  const voiceStatus = voiceError || (transcribing
    ? t("assistant_voice_transcribing")
    : recording
      ? t("assistant_voice_recording")
      : speechLoading
        ? t("assistant_speech_loading")
        : speaking
          ? t("assistant_voice_speaking")
          : "");

  return (
    <>
      {open && (
        <section
          className="fixed bottom-20 right-3 z-40 flex h-[min(68vh,34rem)] w-[calc(100vw-1.5rem)] flex-col overflow-hidden rounded-2xl border border-border bg-card text-card-foreground shadow-2xl sm:bottom-24 sm:right-6 sm:h-[min(68vh,36rem)] sm:w-[390px]"
          aria-label={t("assistant_title")}
          data-testid="floating-assistant-panel"
        >
          <header className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
            <div className="flex min-w-0 items-center gap-2">
              <Bot className="h-5 w-5 shrink-0 text-primary" />
              <h2 className="truncate text-sm font-semibold">{t("assistant_title")}</h2>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0"
              onClick={() => setOpen(false)}
              aria-label={t("assistant_close")}
              data-testid="floating-assistant-close"
            >
              <X className="h-4 w-4" />
            </Button>
          </header>

          <div
            className="flex-1 space-y-3 overflow-y-auto bg-muted/20 px-4 py-4"
            aria-live="polite"
            data-testid="floating-assistant-messages"
          >
            {messages.length === 0 && !pendingQuestion && (
              <div className="flex h-full flex-col items-center justify-center gap-2 px-4 text-center text-sm text-muted-foreground">
                <Bot className="h-7 w-7 text-primary/70" />
                <p>{t("assistant_chat_welcome")}</p>
              </div>
            )}
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
                data-testid={`floating-assistant-message-${message.role}`}
              >
                <div className={`max-w-[86%] ${message.role === "user" ? "" : "space-y-1"}`}>
                  <p
                    className={`whitespace-pre-wrap rounded-2xl px-3 py-2 text-sm leading-relaxed ${
                      message.role === "user"
                        ? "rounded-br-md bg-primary text-primary-foreground"
                        : "rounded-bl-md border border-border bg-background text-foreground"
                    }`}
                  >
                    {message.text}
                  </p>
                  {message.role === "assistant" && (
                    <div className="flex justify-end">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs text-muted-foreground"
                        onClick={speaking ? stopSpeaking : () => speak(message.text)}
                        aria-label={speaking ? t("assistant_stop_speaking") : t("assistant_speak")}
                        data-testid="floating-assistant-speak"
                      >
                        <Volume2 className="h-3.5 w-3.5" />
                        <span className="hidden sm:inline">
                          {speechLoading ? t("assistant_speech_loading") : speaking ? t("assistant_voice_speaking") : t("assistant_speak")}
                        </span>
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {pendingQuestion && (
              <div className="flex justify-end">
                <p className="max-w-[86%] whitespace-pre-wrap rounded-2xl rounded-br-md bg-primary px-3 py-2 text-sm leading-relaxed text-primary-foreground">
                  {pendingQuestion}
                </p>
              </div>
            )}
            {loading && (
              <div className="flex justify-start">
                <p className="rounded-2xl rounded-bl-md border border-border bg-background px-3 py-2 text-sm text-muted-foreground">
                  {t("assistant_thinking")}
                </p>
              </div>
            )}
            {pendingLogAction && (
              <div className="flex justify-start">
                <div className="max-w-[86%]">
                  <AssistantPendingLogAction
                    action={pendingLogAction}
                    onConfirm={handleConfirmPendingLog}
                    onCancel={handleCancelPendingLog}
                    t={t}
                    compact
                  />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="shrink-0 border-t border-border bg-card p-3">
            <div className="flex items-end gap-2">
              <Textarea
                id="floating-assistant-question"
                value={question}
                onChange={handleTypedChange}
                placeholder={t("assistant_chat_placeholder")}
                aria-label={t("assistant_chat_placeholder")}
                maxLength={1000}
                rows={2}
                className="min-h-[44px] max-h-24 resize-none"
                disabled={loading}
                data-testid="floating-assistant-question"
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                className={`h-11 w-11 shrink-0 ${recording ? "border-primary bg-primary/10 text-primary" : ""}`}
                onPointerDown={handleMicStart}
                onPointerUp={handleMicStop}
                onPointerCancel={handleMicStop}
                onPointerLeave={handleMicStop}
                onKeyDown={(event) => {
                  if ((event.key === " " || event.key === "Enter") && !recording) handleMicStart(event);
                }}
                onKeyUp={(event) => {
                  if (event.key === " " || event.key === "Enter") handleMicStop(event);
                }}
                disabled={loading || transcribing}
                aria-label={recording ? t("assistant_voice_stop") : t("assistant_voice_start")}
                data-testid="floating-assistant-mic"
              >
                {listening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
              </Button>
              <Button
                className="btn-primary h-11 shrink-0 px-4"
                onClick={handleAsk}
                disabled={loading || !question.trim() || remaining === 0}
                aria-label={loading ? t("assistant_thinking") : t("assistant_chat_ask")}
                data-testid="floating-assistant-submit"
              >
                <MessageSquare className="h-4 w-4" />
                <span>{loading ? "..." : t("assistant_chat_ask")}</span>
              </Button>
            </div>
            <p
              className={`mt-1 h-4 text-xs leading-4 ${voiceError ? "text-destructive" : "text-muted-foreground"}`}
              data-testid={voiceError ? "floating-assistant-voice-error" : "floating-assistant-listening"}
            >
              {voiceStatus || "\u00a0"}
            </p>
            {error && (
              <p className="text-sm text-destructive" data-testid="floating-assistant-error">
                {error}
              </p>
            )}
            {remaining === 0 && !error && (
              <p className="text-sm text-destructive">{t("assistant_limit_reached")}</p>
            )}
            <p className="text-xs text-muted-foreground" data-testid="floating-assistant-usage">
              {usageCount} {t("assistant_chat_of")} {dailyLimit} {t("assistant_chat_used_today")} {"\u00b7"} {remaining}{" "}
              {t("assistant_chat_remaining")}
            </p>
            <p className="text-[11px] leading-relaxed text-muted-foreground">{t("assistant_disclaimer")}</p>
          </div>
        </section>
      )}

      {!open && (
        <Button
          type="button"
          className="btn-primary fixed bottom-4 right-4 z-40 h-12 rounded-full px-4 shadow-xl sm:bottom-6 sm:right-6"
          onClick={() => setOpen(true)}
          aria-label={t("assistant_open")}
          data-testid="floating-assistant-open"
        >
          <Bot className="h-5 w-5" />
          <span className="hidden sm:inline">{t("nav_assistant")}</span>
        </Button>
      )}
    </>
  );
}
